import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import Pagination, { getPageSize } from '../../Pagination';
import type { AwCtx } from '../ctx';
import { amt, card, errBox, inp, note, num, pillAmber, pillGreen, pillGrey, table, td, th } from '../ui';
import { actBtn, Dialog, field, Foot, lbl } from '../dialog';
import NewDocBtn from '../NewDocBtn';
import { AccountCombo, accountLabel, ComboAccount } from '../AccountCombo';
import { vatOptions } from '../../MerchantDoc/shared';
import { isReadOnlySeat } from './fixedAssets/shared';

// ── Sales › Products & accounts — the company's catalogue and the income account each entry books to ───────────
// (owner 2026-10-15, phase 3; Add / Edit / status / search: "Product & Service Setup", owner 2026-11-02.)
// A client company's products and services carry a CODE — their charge identity — and each code is given the income
// account it books to. ⛔ Strictly this company's own chart: the picker lists the active income accounts lines may book
// to (GET /product-accounts), and the server asks the same question of whatever is sent (portalDocs ownAccount).
// The mapping is NOT a column on the product. revenueAccounts.ts keeps two questions apart — WHAT a charge is (an
// identity, matched and never interpreted) and WHICH account it belongs to (per client configuration) — so the product
// carries the identity and a revenue_accounts row carries the account, written with the entry in one transaction by
// POST / PUT /products. The draft generator keeps reading the one store it always read.
// The form is where a code and an account are required. The portal's form, the importer and Telegram still make entries
// without them; those are listed here as incomplete until someone fills them in.

type Row = { id: string; name: string; name_th: string; unit: string; unit_price: number; code: string; keyable?: boolean; locked?: boolean;
  item_type: string; description: string; vat_category: string; active: number };
type Mapped = { id: string; accountCode: string; nameTh: string; nameEn: string; scope: string; usable?: boolean };
type VatWorld = { thai: boolean; registered: boolean };
type Form = { id: string; type: string; code: string; name: string; nameTh: string; description: string; unit: string; unitPrice: string;
  vat: string; account: string; active: boolean; locked: boolean };

// As server/src/services/revenueAccounts.ts chargeKey: A-1 and A1 are one charge to the books, so they are one code here.
// Answers off the server are plain objects, and a code may normalise to 'constructor': read only what is really there.
const ownOf = <T,>(o: Record<string, T>, k: string): T | undefined => (Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined);
const keyOf = (v: string): string => String(v || '').toLowerCase().replace(/[^a-z0-9฀-๿]+/g, '');
const emptyForm = (vat: VatWorld): Form => ({ id: '', type: 'service', code: '', name: '', nameTh: '', description: '', unit: '', unitPrice: '',
  vat: vat.registered ? 'standard' : 'none', account: '', active: true, locked: false });

const Req = () => <span aria-hidden style={{ color: '#B42318', marginLeft: 3 }}>*</span>;

/** "+ Create New Account": on this company's chart, as an income account the form can pick at once, without leaving it. */
function NewAccountDialog({ ctx, taken, onClose, onCreated }: { ctx: AwCtx; taken: string[]; onClose: () => void; onCreated: (code: string) => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [nameTh, setNameTh] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const codeTaken = !!code.trim() && taken.includes(code.trim());
  const ready = !!code.trim() && !!nameTh.trim() && !codeTaken;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await api.acctAddAccount(ctx.clientId, { code: code.trim(), nameTh: nameTh.trim(), nameEn: nameEn.trim(), cls: 'income', type: 'Revenue' });
      onCreated(code.trim());
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('aw_pacc_na_title')} onClose={onClose} busy={busy} dirty={!!(code || nameTh || nameEn)}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-plus" label={t('aw_pa_na_save')} t={t} />}>
      <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 12 }} data-pa-new-account>
        <div style={note}>{t('aw_pa_na_note')}</div>
        {err && <div style={errBox}>{err}</div>}
        <div>
          <label htmlFor="pa-na-code" style={lbl}>{t('aw_pacc_na_code')}<Req /></label>
          <input id="pa-na-code" autoFocus value={code} onChange={e => setCode(e.target.value)} style={field} />
          {codeTaken && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_pacc_na_code_taken', { code: code.trim() })}</div>}
        </div>
        <div>
          <label htmlFor="pa-na-th" style={lbl}>{t('aw_pacc_na_name_th')}<Req /></label>
          <input id="pa-na-th" value={nameTh} onChange={e => setNameTh(e.target.value)} style={field} />
        </div>
        <div>
          <label htmlFor="pa-na-en" style={lbl}>{t('aw_pacc_na_name_en')}</label>
          <input id="pa-na-en" value={nameEn} onChange={e => setNameEn(e.target.value)} style={field} />
        </div>
      </div>
    </Dialog>
  );
}

function ProductDialog({ ctx, start, original, accounts, chartCodes, codeKeys, accountOff, vat, onClose, onSaved, onAccountAdded }: {
  ctx: AwCtx; start: Form; original: Form; accounts: ComboAccount[]; chartCodes: string[]; codeKeys: Record<string, string[]>; accountOff: string; vat: VatWorld;
  onClose: () => void; onSaved: () => void; onAccountAdded: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState<Form>(start);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tried, setTried] = useState(false);
  const [adding, setAdding] = useState(false);
  const set = (patch: Partial<Form>) => setF(x => ({ ...x, ...patch }));
  const dirty = JSON.stringify(f) !== JSON.stringify(original);
  const key = keyOf(f.code);
  // A code that is not changing is not asked about: entries already sharing a key stay editable (as on the server).
  const codeProblem = !f.code.trim() ? t('aw_pa_required')
    : f.locked ? ''
      : !key ? t('aw_pa_code_unkeyable')
        : key !== keyOf(original.code) && (ownOf(codeKeys, key) || []).some(id => id !== f.id) ? t('aw_pa_code_taken') : '';
  const price = f.unitPrice.trim() === '' ? 0 : Number(f.unitPrice);
  const priceBad = !Number.isFinite(price) || price < 0;
  const ok = !codeProblem && !!f.name.trim() && !!f.account && !priceBad && (!vat.thai || !!f.vat);
  const submit = async () => {
    setTried(true);
    if (!ok || busy) return;
    setBusy(true); setErr('');
    const body = { type: f.type, code: f.code.trim(), name: f.name.trim(), nameTh: f.nameTh.trim(), description: f.description.trim(), unit: f.unit.trim(),
      unitPrice: price, vatCategory: f.vat, account: f.account, active: f.active };
    try {
      if (f.id) await api.awUpdateProduct(ctx.clientId, f.id, body);
      else await api.awCreateProduct(ctx.clientId, body);
      onSaved();
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  const miss = (bad: boolean) => (tried && bad ? { borderColor: '#B42318' } : {});
  const hint = (bad: boolean, text: string) => (tried && bad ? <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{text}</div> : null);
  return (
    <Dialog fullscreen title={f.id ? t('aw_pa_edit_title') : t('aw_pa_add_title')} docNo={f.id ? f.code : undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready icon="ti ti-device-floppy" label={t('aw_doc_save')} t={t} />}>
      <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 12 }} data-pa-form>
        {err && <div style={errBox}>{err}</div>}
        <div>
          <label htmlFor="pa-type" style={lbl}>{t('aw_pa_type')}<Req /></label>
          <select id="pa-type" value={f.type} onChange={e => set({ type: e.target.value })} style={field}>
            <option value="service">{t('aw_pa_type_service')}</option>
            <option value="product">{t('aw_pa_type_product')}</option>
          </select>
        </div>
        <div>
          <label htmlFor="pa-code" style={lbl}>{t('aw_pa_code')}<Req /></label>
          {f.locked ? (
            <>
              <input id="pa-code" value={f.code} readOnly tabIndex={-1} style={{ ...field, color: 'var(--text-secondary)', background: 'var(--bg-app)' }} />
              <div style={{ ...note, marginTop: 3 }}>{t('aw_pa_code_locked')}</div>
            </>
          ) : (
            <>
              <input id="pa-code" value={f.code} placeholder={t('aw_pa_code_ph')} onChange={e => set({ code: e.target.value })}
                style={{ ...field, ...miss(!!codeProblem), ...(f.code.trim() && codeProblem ? { borderColor: '#B42318' } : {}) }} />
              {(f.code.trim() || tried) && codeProblem ? <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }} data-pa-code-problem>{codeProblem}</div> : null}
            </>
          )}
        </div>
        <div>
          <label htmlFor="pa-name" style={lbl}>{t('aw_pa_name')}<Req /></label>
          <input id="pa-name" value={f.name} onChange={e => set({ name: e.target.value })} style={{ ...field, ...miss(!f.name.trim()) }} />
          {hint(!f.name.trim(), t('aw_pa_required'))}
        </div>
        <div>
          <label htmlFor="pa-name-th" style={lbl}>{t('aw_pa_name_th')}</label>
          <input id="pa-name-th" value={f.nameTh} onChange={e => set({ nameTh: e.target.value })} style={field} />
        </div>
        <div>
          <label htmlFor="pa-desc" style={lbl}>{t('aw_pa_description')}</label>
          <textarea id="pa-desc" rows={2} value={f.description} onChange={e => set({ description: e.target.value })} style={{ ...field, resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px' }}>
            <label htmlFor="pa-unit" style={lbl}>{t('aw_pa_unit')}</label>
            <input id="pa-unit" value={f.unit} onChange={e => set({ unit: e.target.value })} style={field} />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <label htmlFor="pa-price" style={lbl}>{t('aw_pa_price')}</label>
            <input id="pa-price" type="number" min={0} step="any" value={f.unitPrice} onChange={e => set({ unitPrice: e.target.value })}
              style={{ ...field, textAlign: 'right', ...miss(priceBad) }} />
            {hint(priceBad, t('aw_pa_price_bad'))}
          </div>
        </div>
        {/* Outside Thailand a line carries the rate typed on it and the catalogue says nothing, so nothing is asked. */}
        {vat.thai && (
          <div>
            <label htmlFor="pa-vat" style={lbl}>{t('aw_pa_vat')}<Req /></label>
            {vat.registered ? (
              <select id="pa-vat" value={f.vat} onChange={e => set({ vat: e.target.value })} style={field}>
                {vatOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <>
                <input id="pa-vat" readOnly tabIndex={-1} value={vatOptions(t).find(o => o.value === 'none')?.label || ''}
                  style={{ ...field, color: 'var(--text-secondary)', background: 'var(--bg-app)' }} />
                <div style={{ ...note, marginTop: 3 }}>{t('aw_pa_vat_unregistered')}</div>
              </>
            )}
          </div>
        )}
        <div>
          <label htmlFor="pa-account" style={lbl}>{t('aw_pa_sales_account')}<Req /></label>
          {/* An empty chart is started by copying the house template, and that copy runs only on an empty chart: an account added
              ahead of it would stop it. So nothing is offered here until the chart exists. */}
          <AccountCombo id="pa-account" accounts={accounts} value={f.account} onChange={code => set({ account: code })} invalid={(tried || !!accountOff) && !f.account}
            placeholder={t('aw_pa_acct_ph')} emptyLabel={t('aw_pa_acct_none')}
            createLabel={chartCodes.length ? t('aw_pa_new_account') : undefined} onCreate={chartCodes.length ? () => setAdding(true) : undefined} />
          {!chartCodes.length && <div style={{ ...note, marginTop: 3, color: '#92400E' }}>{t('aw_pa_chart_empty')}</div>}
          {accountOff && !f.account && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }} data-pa-account-off>{t('aw_pa_account_off_hint', { code: accountOff })}</div>}
          {!accountOff && hint(!f.account, t('aw_pa_required'))}
        </div>
        <div>
          <label htmlFor="pa-status" style={lbl}>{t('aw_pa_status')}<Req /></label>
          <select id="pa-status" value={f.active ? '1' : '0'} onChange={e => set({ active: e.target.value === '1' })} style={field}>
            <option value="1">{t('aw_pa_active')}</option>
            <option value="0">{t('aw_pa_inactive')}</option>
          </select>
        </div>
      </div>
      {adding && (
        <NewAccountDialog ctx={ctx} taken={chartCodes} onClose={() => setAdding(false)}
          onCreated={async code => { await onAccountAdded(); set({ account: code }); setAdding(false); }} />
      )}
    </Dialog>
  );
}

export function ProductsAccountsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Row[] | null>(null);
  const [accounts, setAccounts] = useState<ComboAccount[]>([]);
  const [byCode, setByCode] = useState<Record<string, Mapped>>({});
  const [codeKeys, setCodeKeys] = useState<Record<string, string[]>>({});
  const [chartCodes, setChartCodes] = useState<string[]>([]);
  const [vat, setVat] = useState<VatWorld>({ thai: true, registered: true });
  const [editing, setEditing] = useState<Form | null>(null);
  const [accountOff, setAccountOff] = useState('');
  const asked = useRef(0);   // a slower, earlier answer must not overwrite a later search's
  const [answered, setAnswered] = useState(false);   // the form reads the chart from the list's answer, so it waits for one
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [total, setTotal] = useState(0);
  // ⛔ A read-only seat (a client's own books login, the auditor) reads everything here as TEXT and is offered nothing
  // that writes: the server refuses that seat every write, and each refusal pages the admins (middleware.ts).
  const ro = isReadOnlySeat(ctx);

  // What to ask for is read when asking: a save or a status change coming back after the page moved reloads the page shown now.
  const wanted = useRef({ page, limit, q });
  wanted.current = { page, limit, q };
  const load = async () => {
    const mine = ++asked.current;
    try {
      const w = wanted.current;
      const d: any = await api.awProductAccounts(ctx.clientId, w.page, w.limit, w.q);
      if (mine !== asked.current) return;
      setProducts(d.products || []);
      setTotal(Number(d.total) || 0);
      setAccounts(d.incomeAccounts || []);
      setByCode(d.byCode || {});
      setCodeKeys(d.codeKeys || {});
      setChartCodes(d.chartCodes || []);
      if (d.vat) setVat(d.vat);
      setAnswered(true);
      setErr('');
    } catch (e: any) { if (mine === asked.current) { setProducts([]); setErr(e.message); } }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [ctx.clientId, ctx.reload, page, limit, q]);
  // Searches as it is typed, in the database (so across every page), once the typing rests.
  useEffect(() => {
    const h = setTimeout(() => { setQ(typed.trim()); setPage(1); }, 300);
    return () => clearTimeout(h);
  }, [typed]);

  const openEdit = (p: Row) => {
    const m = ownOf(byCode, p.code);
    // An account since switched off cannot be saved again, so the form opens asking for another and says why.
    const own = m?.scope === 'client' ? m : undefined;
    setAccountOff(own && own.usable === false ? own.accountCode : '');
    setEditing({ id: p.id, type: p.item_type === 'product' ? 'product' : 'service', code: p.code, name: p.name, nameTh: p.name_th || '', description: p.description || '',
      unit: p.unit || '', unitPrice: Number(p.unit_price) > 0 ? String(p.unit_price) : '', vat: vat.registered ? p.vat_category : 'none',
      account: own && own.usable !== false ? own.accountCode : '', active: !!Number(p.active), locked: !!p.locked });
  };
  const setStatus = async (p: Row, active: boolean) => {
    setBusy(p.id); setErr('');
    try { await api.awSetProductStatus(ctx.clientId, p.id, active); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const rows = products || [];
  const vatLabel = useMemo(() => new Map(vatOptions(t).map(o => [o.value, o.label])), [t]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={note}><i className="ti ti-book" aria-hidden /> {t('aw_pa_hint')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input style={{ ...inp, flex: '1 1 240px', maxWidth: 360 }} type="search" value={typed} placeholder={t('aw_pa_search_ph')}
          aria-label={t('aw_pa_search_ph')} onChange={e => setTyped(e.target.value)} data-pa-search />
        <div style={{ flex: 1 }} />
        {/* The toolbar's create button, top right as on the Sales lists around it (owner 2026-11-02, "Standardize New
            Document Button", the later ruling; it replaces the same day's 统一用+号, which put a floating + here). It keeps
            its own words: a product is not a document. The form reads the chart from the list's answer, so it is offered
            once there is one. */}
        {!ro && answered && <NewDocBtn onClick={() => { setAccountOff(''); setEditing(emptyForm(vat)); }} title={t('aw_pa_add_title')} label={t('aw_pa_add_title')} />}
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {products === null ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : !rows.length ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('aw_no_rows')}</div> : (
          <table style={table}>
            <thead><tr>
              <th style={th}>{t('aw_pa_code')}</th>
              <th style={th}>{t('aw_pa_name')}</th>
              <th style={th}>{t('aw_pa_type')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('aw_pa_price')}</th>
              {vat.thai && <th style={th}>{t('aw_pa_vat')}</th>}
              <th style={th}>{t('aw_pa_sales_account')}</th>
              <th style={th}>{t('aw_pa_status')}</th>
              {!ro && <th style={th} aria-label={t('aw_pa_edit')} />}
            </tr></thead>
            <tbody>
              {rows.map(p => {
                const mapped = ownOf(byCode, p.code);
                const on = !!Number(p.active);
                const gap = !p.code.trim() ? t('aw_pa_need_code') : p.keyable === false ? t('aw_pa_code_unkeyable') : !mapped ? t('aw_pa_need_account')
                  : mapped.usable === false ? t('aw_pa_account_off') : '';
                return (
                  <tr key={p.id} style={on ? undefined : { opacity: 0.6 }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.code.trim() || '—'}</td>
                    <td style={td}>
                      {p.name}
                      {p.name_th ? <div style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>{p.name_th}</div> : null}
                      {p.description ? <div style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>{p.description}</div> : null}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.item_type === 'product' ? t('aw_pa_type_product') : t('aw_pa_type_service')}</td>
                    <td style={num}>{Number(p.unit_price) > 0 ? amt(p.unit_price) : '—'}</td>
                    {vat.thai && <td style={{ ...td, whiteSpace: 'nowrap' }}>{vatLabel.get(p.vat_category) || '—'}</td>}
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {/* What the books route this code to: its own account, the firm's default, or nothing. */}
                      {mapped?.scope === 'client' ? <span>{accountLabel({ code: mapped.accountCode, name_th: mapped.nameTh, name_en: mapped.nameEn })}</span>
                        : mapped?.scope === 'firm' ? <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('aw_pa_firm_default', { code: mapped.accountCode })}</span>
                        : <span style={{ color: 'var(--text-tertiary)' }}>{t('aw_pa_none')}</span>}
                      {!ro && gap && <div style={{ marginTop: 3 }}><span style={pillAmber} title={gap}>{t('aw_pa_incomplete')}</span> <span style={{ fontSize: 11.5, color: '#92400E' }}>{gap}</span></div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}><span style={on ? pillGreen : pillGrey}>{on ? t('aw_pa_active') : t('aw_pa_inactive')}</span></td>
                    {!ro && (
                      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button style={actBtn} onClick={() => openEdit(p)} data-pa-edit>{t('aw_pa_edit')}</button>{' '}
                        <button style={{ ...actBtn, opacity: busy === p.id ? 0.5 : 1 }} disabled={busy === p.id} onClick={() => setStatus(p, !on)} data-pa-status>
                          {on ? t('aw_pa_deactivate') : t('aw_pa_activate')}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {products && <Pagination page={page} total={total} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_pa_foot')}</div>
      {editing && (
        <ProductDialog ctx={ctx} start={editing} original={editing} accounts={accounts} chartCodes={chartCodes} codeKeys={codeKeys} accountOff={accountOff} vat={vat}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onAccountAdded={load} />
      )}
    </div>
  );
}
